// ================================================================
//  reportHtml — builds the printable Service / Expense report pages.
//
//  Ported 1:1 from the mobile app (app/(home)/reports.tsx) so both
//  apps print the same layout. Differences from the mobile version:
//   • user-entered text is HTML-escaped (a "<" in a remark can no
//     longer break the page)
//   • numbers are coerced, so a text column can't crash toFixed()
//   • A4 print rules: table header repeats on every page, rows are
//     never split across pages
// ================================================================

const SERVICE_GIVEN_MAP = { 1: "ONSITE", 2: "ONLINE" };

export const escapeHtml = (v) =>
    String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// dd/mm/yy. Plain "YYYY-MM-DD" values are formatted directly (no timezone
// shift); full timestamps go through Date so they show in local time.
export const toDisplay = (iso) => {
    if (!iso) return "";
    const s = String(iso);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

// Local-time YYYY-MM-DD (toISOString() would use UTC and can be a day off
// around midnight in India).
export const toISODate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

export const firstOfMonth = () => {
    const d = new Date();
    d.setDate(1);
    return toISODate(d);
};

const money = (n) => (n > 0 ? n.toFixed(2) : "");

const BASE_STYLE = `
    @page { size: A4; margin: 12mm; }
    body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; color: #000; }
    .page-no { text-align: right; font-size: 10px; margin-bottom: 4px; }
    .company { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 4px; }
    .subtitle { text-align: center; font-size: 12px; font-weight: bold; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td { border: 1px solid #000; padding: 3px 5px; font-size: 10px; }
    th { background-color: #f0f0f0; font-weight: bold; text-align: center; }
    .total-row td { font-weight: bold; background-color: #f0f0f0; }
    .balance { text-align: center; font-size: 13px; font-weight: bold; margin-top: 10px; }
    @media print { body { margin: 0; } }
`;

// rows: [{ service_date, client_name, reason, service_given, pay_mode,
//          gst_amount, net_amount, share_amount }]
export function buildServiceReportHtml(rows, partnerName, dateFrom, dateTo) {
    let totalCash = 0, totalBank = 0, totalGst = 0, totalNet = 0, totalShare = 0;

    const rowsHTML = rows.map((row, i) => {
        const net = num(row.net_amount);
        const gst = num(row.gst_amount);
        const share = num(row.share_amount);
        const cash = row.pay_mode === 1 ? net : 0;
        const bank = row.pay_mode !== 1 ? net : 0;
        totalCash += cash; totalBank += bank;
        totalGst += gst; totalNet += net; totalShare += share;

        return `<tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${toDisplay(row.service_date)}</td>
      <td>${escapeHtml(row.client_name)}</td>
      <td>${escapeHtml(row.reason)}</td>
      <td style="text-align:center">${SERVICE_GIVEN_MAP[row.service_given] ?? ""}</td>
      <td style="text-align:right">${money(cash)}</td>
      <td style="text-align:right">${money(bank)}</td>
      <td style="text-align:right">${money(gst)}</td>
      <td style="text-align:right">${money(net)}</td>
      <td style="text-align:right">${money(share)}</td>
    </tr>`;
    }).join("");

    const balance = totalNet - totalShare;
    const title = `Service Report of ${partnerName} from ${toDisplay(dateFrom)} to ${toDisplay(dateTo)}`;

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>${escapeHtml(title)}</title>
  <style>${BASE_STYLE}</style></head><body>
  <div class="page-no">Page No. 1</div>
  <div class="company">XQlusive Software Solutions</div>
  <div class="subtitle">${escapeHtml(title)}</div>
  <table>
    <thead><tr>
      <th style="width:25px">Sl</th><th style="width:60px">Date</th>
      <th style="width:160px">Customer</th><th>Remarks</th>
      <th style="width:55px">Mode</th><th style="width:60px">Cash</th>
      <th style="width:60px">Bank</th><th style="width:40px">GST</th>
      <th style="width:65px">Net Amt</th><th style="width:50px">Share</th>
    </tr></thead>
    <tbody>
      ${rowsHTML}
      <tr class="total-row">
        <td colspan="5" style="text-align:center">TOTAL</td>
        <td style="text-align:right">${totalCash.toFixed(2)}</td>
        <td style="text-align:right">${totalBank.toFixed(2)}</td>
        <td style="text-align:right">${totalGst.toFixed(2)}</td>
        <td style="text-align:right">${totalNet.toFixed(2)}</td>
        <td style="text-align:right">${totalShare.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>
  <div class="balance">BALANCE : ${balance >= 0 ? "" : "-"}${Math.abs(balance).toFixed(2)}</div>
  </body></html>`;
}

// rows: [{ expense_date, place, remarks, km, travel, food, toll }]
export function buildExpenseReportHtml(rows, partnerName, dateFrom, dateTo) {
    let totalKm = 0, totalTravel = 0, totalFood = 0, totalToll = 0, totalNet = 0;

    const rowsHTML = rows.map((row, i) => {
        const km = num(row.km), travel = num(row.travel), food = num(row.food), toll = num(row.toll);
        const net = travel + food + toll;
        totalKm += km; totalTravel += travel;
        totalFood += food; totalToll += toll; totalNet += net;

        return `<tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${toDisplay(row.expense_date)}</td>
      <td>${escapeHtml(row.place)}</td>
      <td>${escapeHtml(row.remarks)}</td>
      <td style="text-align:right">${money(km)}</td>
      <td style="text-align:right">${money(travel)}</td>
      <td style="text-align:right">${money(food)}</td>
      <td style="text-align:right">${money(toll)}</td>
      <td style="text-align:right">${money(net)}</td>
    </tr>`;
    }).join("");

    const title = partnerName
        ? `Expense Report of ${partnerName} from ${toDisplay(dateFrom)} to ${toDisplay(dateTo)}`
        : `Expense Report from ${toDisplay(dateFrom)} to ${toDisplay(dateTo)}`;

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>${escapeHtml(title)}</title>
  <style>${BASE_STYLE}</style></head><body>
  <div class="page-no">Page No. 1</div>
  <div class="company">XQlusive Software Solutions</div>
  <div class="subtitle">${escapeHtml(title)}</div>
  <table>
    <thead><tr>
      <th style="width:25px">Sl</th><th style="width:60px">Date</th>
      <th style="width:120px">Place</th><th>Remarks</th>
      <th style="width:45px">KM</th><th style="width:60px">Fuel</th>
      <th style="width:60px">Food</th><th style="width:50px">Toll</th>
      <th style="width:65px">Net Amt</th>
    </tr></thead>
    <tbody>
      ${rowsHTML}
      <tr class="total-row">
        <td colspan="4" style="text-align:center">TOTAL</td>
        <td style="text-align:right">${totalKm.toFixed(2)}</td>
        <td style="text-align:right">${totalTravel.toFixed(2)}</td>
        <td style="text-align:right">${totalFood.toFixed(2)}</td>
        <td style="text-align:right">${totalToll.toFixed(2)}</td>
        <td style="text-align:right">${totalNet.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>
  </body></html>`;
}
